// Sammendraget: meldt tid, faktisk tid og avviket mellom dem.
//
// HVA AVVIKET ER: `meldtMinutter` er summen av alle økter. `sammenslattMinutter`
// er den samme tiden der overlappende økter telles ÉN gang. Differansen er altså
// tid som er ført to ganger — en øvelse meldt 18–22 og en git-avledet økt 20–23
// kan ikke begge være sanne for de to timene de deler.
//
// Avviket er ikke en feil klienten skal rette opp. Verktøyet vet ikke hvilken av
// de to føringene som er riktig; bare den som førte dem vet det. Derfor er
// jobben her å FORKLARE tallet, ikke å skjule eller «fikse» det.
//
// Ingen av tallene regnes her. `timerTekst` kommer fra @skade/dugnad, og timer
// og summer kommer ferdig avrundet fra tjenesten — som runder ÉN gang til slutt.
import { timerTekst } from '@skade/dugnad';

export interface Avvik {
  meldtMinutter: number;
  sammenslattMinutter: number;
  avvikMinutter: number;
  /** Hvor stor del av den meldte tiden som er dobbeltført (0–1). */
  andel: number;
  harAvvik: boolean;
  /** Kort merkelapp: «Ingen dobbeltføring» / «2 t dobbeltført». */
  merkelapp: string;
  /** Hele setningen som skal stå i UI-et under tallet. */
  forklaring: string;
}

const INGEN = 'Ingen dobbeltføring';

/**
 * Tolk meldt mot faktisk tid.
 *
 * Negativt avvik skal ikke kunne skje — sammenslått tid kan aldri overstige
 * meldt tid. Skjer det likevel, er det tjenesten som har regnet feil, og da sier
 * vi DET framfor å vise «−30 min dobbeltført» som om det var meningsfullt.
 */
export function tolkAvvik(meldtMinutter: number, sammenslattMinutter: number): Avvik {
  const meldt = Number.isFinite(meldtMinutter) ? meldtMinutter : 0;
  const slått = Number.isFinite(sammenslattMinutter) ? sammenslattMinutter : 0;
  const avvik = meldt - slått;

  if (avvik < 0) {
    return {
      meldtMinutter: meldt, sammenslattMinutter: slått, avvikMinutter: avvik,
      andel: 0, harAvvik: true, merkelapp: 'Ugyldig avvik',
      forklaring: 'Tjenesten oppgir mer faktisk tid enn meldt tid. Det er umulig — '
        + 'sammenslått tid kan ikke overstige summen. Meld fra om avviket framfor å bruke tallene.',
    };
  }
  if (avvik === 0) {
    return {
      meldtMinutter: meldt, sammenslattMinutter: slått, avvikMinutter: 0,
      andel: 0, harAvvik: false, merkelapp: INGEN,
      forklaring: 'Ingen av øktene overlapper i tid, så meldt tid og faktisk tid er den samme.',
    };
  }
  return {
    meldtMinutter: meldt, sammenslattMinutter: slått, avvikMinutter: avvik,
    andel: meldt > 0 ? avvik / meldt : 0,
    harAvvik: true,
    merkelapp: `${timerTekst(avvik)} dobbeltført`,
    forklaring: `${timerTekst(avvik)} er ført på to økter som overlapper i tid. `
      + `Meldt tid (${timerTekst(meldt)}) er summen av alle økter; faktisk tid (${timerTekst(slått)}) `
      + 'teller hver time én gang. Ingen av føringene er slettet — kontrollér selv hvilken som er riktig.',
  };
}

// ── Rader fra `GET /sammendrag` ─────────────────────────────────────────────

/** Tallene tjenesten sender for hver gruppe (person, type, oppdragskode). */
export interface Tall {
  antall: number;
  meldtMinutter: number;
  meldtTimer: number;
  meldtTekst: string;
  sammenslattMinutter: number;
  sammenslattTimer: number;
  sammenslattTekst: string;
  avvikMinutter: number;
  avvikTimer: number;
}

const TOMME_TALL: Tall = {
  antall: 0, meldtMinutter: 0, meldtTimer: 0, meldtTekst: '0 min',
  sammenslattMinutter: 0, sammenslattTimer: 0, sammenslattTekst: '0 min',
  avvikMinutter: 0, avvikTimer: 0,
};

function talltekst(v: unknown, standard: string): string {
  return typeof v === 'string' && v ? v : standard;
}
function n(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/**
 * Les tallene fra en rad. Tekstfeltene (`meldtTekst` …) er tjenestens egne — de
 * er formatert av `timerTekst` der, og skal ikke regnes om her. Mangler de,
 * lager vi dem av minuttene med SAMME funksjon, ikke av timetallene: et avrundet
 * timetall er allerede tapt presisjon.
 */
export function lesTall(rå: unknown): Tall {
  if (!rå || typeof rå !== 'object') return { ...TOMME_TALL };
  const r = rå as Record<string, unknown>;
  const meldt = n(r.meldtMinutter);
  const slått = n(r.sammenslattMinutter);
  return {
    antall: n(r.antall),
    meldtMinutter: meldt,
    meldtTimer: n(r.meldtTimer),
    meldtTekst: talltekst(r.meldtTekst, timerTekst(meldt)),
    sammenslattMinutter: slått,
    sammenslattTimer: n(r.sammenslattTimer),
    sammenslattTekst: talltekst(r.sammenslattTekst, timerTekst(slått)),
    avvikMinutter: n(r.avvikMinutter),
    avvikTimer: n(r.avvikTimer),
  };
}

export interface TypeRad extends Tall { type: string; navn: string }
export interface KodeRad extends Tall { oppdragskode: string }
export interface PersonRad extends Tall {
  personId: string;
  perType: TypeRad[];
  perOppdragskode: KodeRad[];
}

export interface Sammendrag {
  fra: number | null;
  til: number | null;
  antallOkter: number;
  utelatt: number;
  utelattForklaring: string;
  personer: PersonRad[];
  perType: TypeRad[];
  totalt: Tall;
}

function liste(v: unknown): Record<string, unknown>[] {
  return Array.isArray(v) ? v.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object') : [];
}
function tidspunkt(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export function lesSammendrag(rå: unknown): Sammendrag {
  const r = (rå && typeof rå === 'object' ? rå : {}) as Record<string, unknown>;
  return {
    fra: tidspunkt(r.fra),
    til: tidspunkt(r.til),
    antallOkter: n(r.antallOkter),
    utelatt: n(r.utelatt),
    // Forklaringen på hva som er utelatt kommer fra tjenesten. Vi finner ikke på
    // en egen: da ville UI-et påstått noe om en filtrering det ikke gjorde.
    utelattForklaring: talltekst(r.utelattForklaring, ''),
    personer: liste(r.personer).map((p) => ({
      personId: typeof p.personId === 'string' ? p.personId : '(ukjent)',
      ...lesTall(p),
      perType: liste(p.perType).map((t) => ({
        type: typeof t.type === 'string' ? t.type : 'annet',
        navn: talltekst(t.navn, typeof t.type === 'string' ? t.type : 'Annet'),
        ...lesTall(t),
      })),
      perOppdragskode: liste(p.perOppdragskode).map((k) => ({
        oppdragskode: talltekst(k.oppdragskode, '(ingen)'),
        ...lesTall(k),
      })),
    })),
    perType: liste(r.perType).map((t) => ({
      type: typeof t.type === 'string' ? t.type : 'annet',
      navn: talltekst(t.navn, typeof t.type === 'string' ? t.type : 'Annet'),
      ...lesTall(t),
    })),
    totalt: lesTall(r.totalt),
  };
}

/** Personene med størst dobbeltføring først — det er dem noen må snakke med. */
export function sorterPåAvvik<T extends Tall>(rader: readonly T[]): T[] {
  return [...rader].sort((a, b) => b.avvikMinutter - a.avvikMinutter || b.meldtMinutter - a.meldtMinutter);
}
